// The queue filter bar.
//
// Every control here maps to a documented query param on GET /api/tickets
// (docs/API.md §3.1) and nothing else: unrecognised params are 400, deliberately, so
// this component is the whole vocabulary of the queue view.
//
// Filters live in the URL, not in component state — an agent sending "the unsolved
// hardware queue" to a colleague sends a link, and the browser's back button steps
// through filter changes the way it does everywhere else.

import { useEffect, useState } from 'react';

import { BASE_STATUSES, STATUS_LABELS, CHANNELS, CHANNEL_LABELS } from './format.js';

// Everything an agent normally means by "my queue". Solved and closed are excluded.
const UNSOLVED = ['new', 'open', 'pending', 'on_hold'];

const SORTS = [
  { value: '-updated_at', label: 'Recently updated' },
  { value: 'updated_at', label: 'Least recently updated' },
  { value: '-created_at', label: 'Newest' },
  { value: 'created_at', label: 'Oldest' },
  // There is no resolution SLA in the live configuration (migration 014); the
  // value matches src/services/tickets.js's ORDER_BY, which still accepts the old
  // `sla_resolution_due_at` spelling as a deprecated alias so a bookmarked or
  // shared queue link keeps working, but nothing here should ever emit it again.
  { value: 'sla_first_response_due_at', label: 'First reply due soonest' },
];

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
        active
          ? 'bg-brand-600 text-white ring-brand-600'
          : 'bg-surface text-ink-secondary ring-line-strong hover:bg-sunken'
      }`}
    >
      {children}
    </button>
  );
}

function toggle(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

/**
 * @param {object} props
 * @param {{statuses: string[], channels: string[], assignee: string, q: string, sort: string}} props.filters
 *   `sort` is '' when nobody has chosen one — see queue-query.js, where that
 *   distinction is what lets a view's own ordering survive.
 * @param {(patch: object) => void} props.onChange
 * @param {Array} props.assignableUsers  GET /api/users?can_be_assigned=true; empty if that call failed.
 * @param {boolean} props.viewActive  Whether a saved view is applied; changes only
 *   what the unset sort option is called.
 */
export default function TicketFilters({ filters, onChange, assignableUsers, viewActive = false }) {
  const [draftQuery, setDraftQuery] = useState(filters.q);

  // The URL is the source of truth, so a back/forward navigation or a cleared filter
  // has to pull the text box back in line with it.
  useEffect(() => { setDraftQuery(filters.q); }, [filters.q]);

  const trimmed = draftQuery.trim();
  // `q` shorter than two characters is 400 INVALID_QUERY_PARAM, so it is never sent.
  const queryTooShort = trimmed.length === 1;

  function submitQuery(event) {
    event.preventDefault();
    if (queryTooShort) return;
    onChange({ q: trimmed });
  }

  const unsolvedActive =
    filters.statuses.length === UNSOLVED.length &&
    UNSOLVED.every((status) => filters.statuses.includes(status));

  const hasFilters =
    filters.statuses.length > 0 ||
    filters.channels.length > 0 ||
    filters.assignee !== '' ||
    filters.q !== '';

  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      {/* items-start, not items-end: the search column is taller than the two select
          columns because it reserves a line below the input for the "type at least two
          characters" hint. Bottom-aligning therefore pushed the Search label above the
          Assignee and Sort labels. Top-aligning puts all three labels on one baseline and
          lets the hint hang below without moving anything. */}
      <div className="flex flex-wrap items-start gap-3">
        <form onSubmit={submitQuery} className="min-w-64 flex-1">
          <label className="field-label" htmlFor="queue-search">
            Search
          </label>
          <div className="flex gap-2">
            <input
              id="queue-search"
              type="search"
              className="field-input"
              placeholder="Ticket number, subject or message text"
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
            />
            <button
              type="submit"
              className="btn-ghost h-10 shrink-0 ring-1 ring-inset ring-line-strong"
              disabled={queryTooShort}
            >
              Search
            </button>
          </div>
          <p className="mt-1 h-4 text-xs text-ink-muted">
            {queryTooShort ? 'Type at least two characters.' : ''}
          </p>
        </form>

        <div className="w-56">
          <label className="field-label" htmlFor="queue-assignee">
            Assignee
          </label>
          <select
            id="queue-assignee"
            className="field-input"
            value={filters.assignee}
            onChange={(event) => onChange({ assignee: event.target.value })}
          >
            <option value="">Anyone</option>
            <option value="me">Assigned to me</option>
            <option value="none">Unassigned</option>
            {assignableUsers.length > 0 && (
              <optgroup label="Agents">
                {assignableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div className="w-52">
          <label className="field-label" htmlFor="queue-sort">
            Sort
          </label>
          <select
            id="queue-sort"
            className="field-input"
            value={filters.sort}
            onChange={(event) => onChange({ sort: event.target.value })}
          >
            {/* The unset option has to be selectable, not just the initial state:
                it is the only way back to a view's own ordering once an agent has
                overridden it. Its label tells the truth about what the server will
                actually do with an absent sort in each case. */}
            <option value="">
              {viewActive ? "View's own order" : 'Recently updated (default)'}
            </option>
            {SORTS.map((sort) => (
              <option key={sort.value} value={sort.value}>
                {sort.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-line pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-secondary">Status</span>
          <Chip
            active={unsolvedActive}
            onClick={() => onChange({ statuses: unsolvedActive ? [] : UNSOLVED })}
          >
            Unsolved
          </Chip>
          <span aria-hidden="true" className="text-line-strong">|</span>
          {BASE_STATUSES.map((status) => (
            <Chip
              key={status}
              active={filters.statuses.includes(status)}
              onClick={() => onChange({ statuses: toggle(filters.statuses, status) })}
            >
              {STATUS_LABELS[status]}
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-secondary">Channel</span>
          {CHANNELS.map((channel) => (
            <Chip
              key={channel}
              active={filters.channels.includes(channel)}
              onClick={() => onChange({ channels: toggle(filters.channels, channel) })}
            >
              {CHANNEL_LABELS[channel]}
            </Chip>
          ))}
        </div>

        {hasFilters && (
          <button
            type="button"
            className="text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline"
            onClick={() => onChange({ statuses: [], channels: [], assignee: '', q: '' })}
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
